/**
 * Widget Library API
 * Template fetching and Widget CRUD operations
 */

import { useState, useEffect, useCallback } from 'react';
import type { WidgetTemplate } from '../types';
import { WIDGET_TEMPLATES, getTemplateById as findTemplateById } from '../constants';

// ============================================================================
// API Configuration
// ============================================================================

export const WIDGET_API_BASE_URL = 'https://worker-widget-config.notifyy.io';

export const API_ENDPOINTS = {
  templates: {
    getAll: '/api/widget-library/templates',
    getById: '/api/widget-library/templates/',
  },
  widgets: {
    base: `${WIDGET_API_BASE_URL}/api/widgets`,
    byId: (id: string) => `${WIDGET_API_BASE_URL}/api/widgets/${id}`,
  },
};

// ============================================================================
// Widget Types
// ============================================================================

export interface WidgetConfig {
  widgetId?: string;
  phoneNumber: string;
  brandName: string;
  brandSubtitle?: string;
  defaultMessage?: string;
  ctaText?: string;
  agentName?: string;
  agentAvatar?: string;
  welcomeMessage?: string;
  onlineStatusText?: string;
  offlineStatusText?: string;

  // Colors
  primaryColor?: string;
  secondaryColor?: string;
  headerColor?: string;
  backgroundColor?: string;
  textColor?: string;
  hoverColor?: string;
  hoverBackgroundColor?: string;
  hoverIconColor?: string;
  badgeColor?: string;
  statusOnlineColor?: string;
  statusOfflineColor?: string;

  // Gradient
  useGradient?: boolean;
  gradientDirection?: string;
  gradientType?: string;

  // Shape & Layout
  buttonShape?: string;
  buttonStyle?: string;
  borderRadius?: number;
  shadowStyle?: string;
  borderStyle?: string;
  borderColor?: string;
  borderWidth?: number;
  iconSize?: number;
  iconPadding?: number;

  // Typography
  fontFamily?: string;
  fontWeight?: number;
  fontSize?: number;
  lineHeight?: number;
  textCase?: string;

  // Icon
  iconType?: string;
  iconColor?: string;
  iconAnimation?: string;
  customSvg?: string;
  emoji?: string;

  // Close Icon
  closeIconType?: string;
  closeIconColor?: string;
  closeIconBackgroundColor?: string;
  closeIconSize?: number;

  // Badge
  badgeType?: string;
  badgeText?: string;
  badgePosition?: string;
  badgeAnimated?: boolean;

  // Position
  position?: string;
  marginTop?: number;
  marginBottom?: number;
  marginRight?: number;
  marginLeft?: number;
  offsetX?: number;
  offsetY?: number;
  zIndex?: number;

  // Animation
  animationType?: string;
  animationDuration?: number;
  animationEasing?: string;
  enablePulse?: boolean;
  pulseColor?: string;
  pulseScale?: number;
  enableNudge?: boolean;
  nudgeDelay?: number;
  nudgeType?: string;

  // Behavior
  openOnMobile?: boolean;
  openByDefault?: boolean;
  showBadge?: boolean;
  soundEnabled?: boolean;
  sessionWindow?: string;

  // Dark mode
  enableDarkMode?: boolean;
  darkModeColors?: {
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
    hoverColor?: string;
    badgeColor?: string;
    statusOnlineColor?: string;
    statusOfflineColor?: string;
    borderColor?: string;
    shadowColor?: string;
  };

  // Accessibility
  respectReducedMotion?: boolean;
  safeAreaHandling?: boolean;

  // Floating Badge
  floatingBadge?: {
    enabled: boolean;
    message: string;
    backgroundColor: string;
    textColor: string;
    fontSize: number;
    glossyEffect: boolean;
    borderColor: string;
    borderWidth: number;
    position: string;
    maxWidth: number;
    textAlign: string;
  };

  createdAt?: string;
  updatedAt?: string;
}

export interface WidgetApiResponse {
  status: boolean;
  message: string;
  data: WidgetConfig;
}

// ============================================================================
// Widget CRUD API Functions
// ============================================================================

/**
 * Create a new widget
 */
export async function createWidget(config: Partial<WidgetConfig>): Promise<WidgetApiResponse> {
  const response = await fetch(API_ENDPOINTS.widgets.base, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    throw new Error(`Failed to create widget: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get widget by ID
 */
export async function getWidgetById(widgetId: string): Promise<WidgetApiResponse> {
  const response = await fetch(API_ENDPOINTS.widgets.byId(widgetId), {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get widget: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update widget by ID
 */
export async function updateWidget(widgetId: string, config: Partial<WidgetConfig>): Promise<WidgetApiResponse> {
  const response = await fetch(API_ENDPOINTS.widgets.byId(widgetId), {
    method: 'PUT',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    throw new Error(`Failed to update widget: ${response.statusText}`);
  }

  return response.json();
}

// ============================================================================
// Template API Functions
// ============================================================================

/**
 * Get all templates
 */
export async function getTemplates(): Promise<WidgetTemplate[]> {
  // Simulated API delay
  await new Promise((resolve) => setTimeout(resolve, 100));
  return WIDGET_TEMPLATES;
}

/**
 * Get template by ID
 */
export async function getTemplateById(id: string): Promise<WidgetTemplate | null> {
  await new Promise((resolve) => setTimeout(resolve, 50));
  return findTemplateById(id) || null;
}

// ============================================================================
// React Hooks
// ============================================================================

/**
 * Hook to fetch all templates
 */
export function useTemplates() {
  const [templates, setTemplates] = useState<WidgetTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(() => {
    setIsLoading(true);
    getTemplates()
      .then(setTemplates)
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { templates, isLoading, error, refetch };
}

/**
 * Hook to fetch a single template
 */
export function useTemplate(id: string | undefined) {
  const [template, setTemplate] = useState<WidgetTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    getTemplateById(id)
      .then(setTemplate)
      .catch(setError)
      .finally(() => setIsLoading(false));
  }, [id]);

  return { template, isLoading, error };
}

// ============================================================================
// Widget Hooks
// ============================================================================

/**
 * Hook for creating a widget
 */
export function useCreateWidget() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<WidgetConfig | null>(null);

  const create = useCallback(async (config: Partial<WidgetConfig>) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await createWidget(config);
      if (response.status) {
        setData(response.data);
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to create widget');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { create, isLoading, error, data };
}

/**
 * Hook for fetching a widget by ID
 */
export function useWidget(widgetId: string | undefined) {
  const [widget, setWidget] = useState<WidgetConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchWidget = useCallback(async (id: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await getWidgetById(id);
      if (response.status) {
        setWidget(response.data);
        return response.data;
      } else {
        throw new Error(response.message || 'Widget not found');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (widgetId) {
      fetchWidget(widgetId);
    }
  }, [widgetId, fetchWidget]);

  return { widget, isLoading, error, refetch: fetchWidget };
}

/**
 * Hook for updating a widget
 */
export function useUpdateWidget() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<WidgetConfig | null>(null);

  const update = useCallback(async (widgetId: string, config: Partial<WidgetConfig>) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await updateWidget(widgetId, config);
      if (response.status) {
        setData(response.data);
        return response.data;
      } else {
        throw new Error(response.message || 'Failed to update widget');
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Unknown error');
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { update, isLoading, error, data };
}
